import { Alert, Modal } from "antd";
import { retryRestClient } from "api/restClient";
import { Button } from "components/Button/Button";
import { StoreProviderForm } from "components/Form/StoreProviderForm";
import { cancelButtonProps } from "components/Form/components/CancelButton/CancelButton";
import { Markdown } from "components/Form/components/Markdown/Markdown";
import { IconCollection } from "components/Icon/IconCollection";
import ExpandableNotification from "components/Notification/ExpandableNotification";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { FeContainerType } from "config/config";
import { getRecordConfig } from "config/resourceUtils";
import { actions } from "containers/Auth/screens/IdentityManagement/IdentityRole.reducer";
import { deleteMutation } from "containers/Auth/screens/IdentityManagement/IdentityRole.type";
import { setActiveProject } from "containers/Projects/Projects.service";
import {
    runListActionWithProgress,
} from "containers/Resources/listActionProgress.utils";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import {
    classifyResources,
    MoveResourcesToLocation,
} from "containers/Resources/components/CommonActions/MoveToLocation/MoveResourcesToLocation";
import { MoveToLocationForm } from "containers/Resources/components/CommonActions/MoveToLocation/MoveToLocationForm";
import { createModalActionSettlement } from "containers/Resources/components/CommonActions/actionModalSettlement";
import { createNotification } from "containers/Resources/components/CommonActions/createNotification";
import {
    ListActionResult,
    WrappedResource,
} from "containers/Resources/components/CommonActions/interfaces";
import PropTypes from "prop-types";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { Dispatch } from "redux";
import { store } from "store";
import { actions as dataStoreActions } from "utils/DataStore/DataStore.reducer";
import { keyToForceUpdate } from "utils/RouterManager/constants";
import { getRelativeUrl, getWorkspaceUrl } from "utils/service";
import { checkContainerItemType } from "utils/utils";
import { Action, ActionData, ActionSignature } from "./Action";
import "./CommonActions.scss";

export const asyncActions = {
    restore: "restored",
    moveToTrash: "moved to trash",
    permanentlyDelete: "permanently deleted",
};

export const forceRecordUpdate = (dispatch?: Dispatch) =>
    dispatch?.({
        type: dataStoreActions.STORE_DATA,
        key: keyToForceUpdate,
        data: Math.floor(Math.random() * 1000),
    });

export const getReadOnlyOrDeleted = (data: ActionData) =>
    data?.isReadOnly || data?.resourceData?.deleted_at != null;

export const isDeleted = (data: ActionData) => data?.resourceData?.deleted_at != null;

const getSingleNormalObject = (resource) =>
    !(
        resource.resourceData?.deleted_at ||
        resource.resourceData?.is_readonly ||
        resource.resourceData?.is_project_readonly ||
        resource.resourceData?.is_bundle_readonly
    );

const getNormalObjects = (resourceList: WrappedResource[]) =>
    resourceList.filter(getSingleNormalObject);

const getGroupedResources = (
    normalResources: any
): { forbiddenDeleted: any[]; allowedDeleted: any[] } =>
    normalResources.reduce(
        (acc: any, resource: any) => {
            if ((getRecordConfig(resource.resourceName) as any).isBulkDeleteForbidden) {
                acc["forbiddenDeleted"].push(resource);
            } else {
                acc["allowedDeleted"].push(resource);
            }
            return acc;
        },
        { forbiddenDeleted: [], allowedDeleted: [] }
    );

/* COMMON SINGLE ACTIONS */

const ExportFullAction = (data: ActionData, downloadStr: string) => {
    const typedWindow: any = window;
    window.open(
        `${typedWindow.REACT_APP_REST_API || ""}${typedWindow.REACT_APP_REST_API_NAMESPACE}/${
            data.resourceType
        }/${data.resourceData?.id}?download=${downloadStr}&by=id`
    );
    return Promise.resolve(null);
};

const genericExportActionSignature = ({ key, downloadStr, label }): ActionSignature => ({
    key,
    action: (data) => ExportFullAction(data, downloadStr),
    label: () => label,
    icon: () => IconCollection.export,
    disableWhen: isDeleted,
});

export const exportFullActionSignature: ActionSignature = genericExportActionSignature({
    key: "export-full",
    downloadStr: "full",
    label: "Export with all resources",
});

export const exportAllActionSignature: ActionSignature = genericExportActionSignature({
    key: "export-all",
    downloadStr: "all",
    label: "Export with all resources",
});

export const exportFullBundleActionSignature: ActionSignature = genericExportActionSignature({
    key: "export-bundle",
    downloadStr: "full_bundle",
    label: "Export with bundle content",
});

type ProjectWarningProps = { isGitEnabled?: boolean } & (
    | { multiple: boolean }
    | { type: FeContainerType }
);

const getProjectPermanentDeleteWarning = (props: ProjectWarningProps) => {
    const text =
        "multiple" in props
            ? "For projects and bundles, this will also delete all resources and the git repository for git enabled containers!"
            : `This will also delete all ${
                  props.isGitEnabled
                      ? `resources in the ${props.type} and the ${props.type}'s git repository`
                      : "its associated resources"
              }!`;
    return (
        <div className="warningMessage">
            <Alert message={text} type="warning" />
        </div>
    );
};

const postDelete = (dispatch: Dispatch, projectDeleted: boolean) => {
    if (projectDeleted) {
        setActiveProject(dispatch, null);
    }
};

export const DeleteAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type: resourcesActions.START_DELETE_RESOURCE,
            promise: { resolve, reject },
            resourceName: data.resourceType,
            id: data.resourceData?.id,
        });
    })
        .then(() => {
            const isContainer = checkContainerItemType(data.resourceType);
            const insertedText = isContainer ? "and all its associated resources" : "";

            if (data.resourceType === "execution" || isContainer) {
                NotificationService.success(
                    `Move to trash of the ${data.resourceType} "${data.resourceData?.name}" ${insertedText} successfully started`
                );
            } else {
                NotificationService.success(
                    `${data.resourceType} "${data.resourceData?.name}" moved to trash`
                );
            }
        })
        .then(() => {
            const projectDeleted = data.resourceType === "project";
            postDelete(dispatch, projectDeleted);
        })
        .catch(console.error);
};

export const deleteActionSignature: ActionSignature = {
    key: "delete",
    action: DeleteAction,
    label: () => "Move to trash",
    icon: () => IconCollection.deleteOutline,
    disableWhen: getReadOnlyOrDeleted,
};

const clearAction = (
    data: ActionData,
    action: "clear_delete" | "clear_restore",
    additionalParams: { permanently?: boolean } | undefined = {}
) => {
    const permanentlyParam =
        additionalParams.permanently !== undefined
            ? `permanently=${additionalParams.permanently}`
            : "";
    const query = permanentlyParam ? `?${permanentlyParam}` : "";
    return new Promise((resolve, reject) =>
        retryRestClient
            .patch(`/${data?.resourceType}/${data?.resourceData?.id}/${action}${query}`)
            .then(() => resolve(undefined))
            .catch((error) => {
                NotificationService.error(<RestErrorNotification error={error} />);
                reject(error);
            })
    );
};

export const clearTrashKey = "clearTrash";
export const clearRestoreKey = "clearRestore";
export const clearDeleteKey = "clearDelete";

const isRecordOrProjectReadonly = (data: ActionData) =>
    data.resourceData?.is_readonly ||
    data.resourceData?.is_project_readonly ||
    data.resourceData?.is_bundle_readonly;

export const clearTrashActionSignature: ActionSignature = {
    key: clearTrashKey,
    action: (data) => clearAction(data, "clear_delete", { permanently: false }),
    label: () => "Clear pending move to trash",
    icon: () => IconCollection.deleteOutline,
    disableWhen: isRecordOrProjectReadonly,
    omitWhen: (data: ActionData) => !data?.resourceData?.pending_trash_identity_id,
};
export const clearRestoreActionSignature: ActionSignature = {
    key: clearRestoreKey,
    action: (data) => clearAction(data, "clear_restore"),
    label: () => "Clear pending restore",
    icon: () => IconCollection.deleteRestore,
    disableWhen: isRecordOrProjectReadonly,
    omitWhen: (data: ActionData) => !data?.resourceData?.pending_restore_identity_id,
};
export const clearDeleteActionSignature: ActionSignature = {
    key: clearDeleteKey,
    action: (data) => clearAction(data, "clear_delete", { permanently: true }),
    label: () => "Clear pending delete",
    icon: () => IconCollection.deleteAlertOutline,
    disableWhen: isRecordOrProjectReadonly,
    omitWhen: (data: ActionData) => !data?.resourceData?.pending_delete_identity_id,
};

export const UndeleteAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type: resourcesActions.START_UNDELETE_RESOURCE,
            promise: { resolve, reject },
            resourceName: data.resourceType,
            id: data.resourceData?.id,
        });
    })
        .then(() => {
            const isContainer = checkContainerItemType(data.resourceType);
            const notificationText = isContainer
                ? `Restore of the ${data.resourceType} "${data.resourceData?.name}" and all its associated resources started`
                : `${data.resourceType} "${data.resourceData?.name}" restored`;
            NotificationService.success(notificationText);
        })
        .catch(console.error);
};

export const undeleteActionSignature: ActionSignature = {
    key: "undelete",
    action: UndeleteAction,
    label: () => "Restore",
    icon: () => IconCollection.deleteRestore,
    disableWhen: (data: ActionData) => data?.isReadOnly || !data?.resourceData?.deleted_at,
};

export const PermanentlyDeleteAction: Action = (data, dispatch, history) => {
    const isContainer = checkContainerItemType(data.resourceType);

    const deleteResourcePermanently = () =>
        new Promise((resolve, reject) => {
            dispatch({
                type: resourcesActions.START_PERMANENTLY_DELETE_RESOURCE,
                promise: { resolve, reject },
                resourceName: data.resourceType,
                id: data.resourceData?.id,
            });
        }).then(() => {
            const insertedText = isContainer ? "and all its associated resources" : "";

            const resourceType = data.resourceType.replace("_", " ");
            NotificationService.success(
                resourceType === "custom_object" || resourceType === "execution" || isContainer
                    ? `Permanent deletion of the ${resourceType} "${data.resourceData?.name}" ${insertedText} successfully started.`
                    : `${resourceType} "${data.resourceData?.name}" permanently deleted`
            );

            if (data?.afterAction) {
                data?.afterAction();
            } else {
                if (data.resourceData?.project_id) {
                    history.push(
                        getRelativeUrl(
                            "project",
                            data.resourceData?.project_id_project?.name ??
                                data.resourceData?.project_id,
                            data.resourceData?.project_id_project?.name ? "name" : "id"
                        )
                    );
                } else if (data.resourceData?.bundle_id) {
                    history.push(
                        getRelativeUrl(
                            "bundle",
                            data.resourceData?.bundle_id_bundle?.name ??
                                data.resourceData?.bundle_id,
                            data.resourceData?.bundle_id_bundle?.name ? "name" : "id"
                        )
                    );
                } else {
                    history.push(getWorkspaceUrl());
                }
            }
        });
    return new Promise((resolve, reject) => {
        const isGitEnabled = data.resourceData.branch_name;

        const promptText = `Are you sure you want to permanently 
            delete the ${data.resourceType} "${data.resourceData?.name}" ?`;
        Modal.confirm({
            title: `Delete ${data.resourceType} permanently`,
            content: isContainer ? (
                <>
                    <div>{promptText}</div>
                    {getProjectPermanentDeleteWarning({
                        isGitEnabled,
                        type: data.resourceType as FeContainerType,
                    })}
                </>
            ) : (
                <div>{promptText}</div>
            ),
            icon: null,
            onOk: () => {
                deleteResourcePermanently()
                    .then((result) => resolve(result))
                    .catch((error) => reject(error));
            },
            onCancel: () => {
                reject("User cancelled delete");
            },
            okText: isContainer
                ? `Delete ${data.resourceType}${
                      isGitEnabled ? ", resources and git repo" : " and its resources permanently"
                  }`
                : "Delete permanently",
            okButtonProps: { className: "button button--danger" },
            cancelButtonProps: cancelButtonProps,
            className: "confirmationModal",
        });
    });
};
export const deletePermanentlyActionSignature: ActionSignature = {
    key: "deletePermanently",
    action: PermanentlyDeleteAction,
    label: () => "Delete Permanently",
    icon: () => IconCollection.deleteAlertOutline,
    disableWhen: (data: ActionData) => data?.isReadOnly,
};

export const MigrateDeprecationsAction: Action = (data, dispatch, history) => {
    return new Promise((resolve, reject) => {
        dispatch({
            type: resourcesActions.START_MIGRATE_DEPRECATIONS,
            promise: { resolve, reject },
            resourceName: data.resourceType,
            id: data.resourceData?.id,
            name: data.resourceData?.name,
        });
    })
        .then(() => {
            // Migrate changed the record ON THE SERVER; re-seed the open editors
            // from the now-stored server state so the code editor shows the
            // migrated content in place (no manual page refresh). A dirty flag
            // alone leaves the editor's pre-migration buffer on screen.
            // See operator-task-fe-migrate-deprecations-code-editor-no-refresh.
            data?.onMigrateDeprecationsApplied?.();
        })
        .catch(console.error);
};

export const migrateDeprecationsKey = "migrateDeprecations";
export const migrateDeprecationsActionSignature: ActionSignature = {
    key: migrateDeprecationsKey,
    action: MigrateDeprecationsAction,
    label: () => "Migrate deprecations",
    icon: () => IconCollection.update,
    disableWhen: (data: ActionData) => data?.isReadOnly,
    omitWhen: (data: ActionData) => !data?.resourceData?.has_deprecation,
};

export const ExportAction: Action = (data, dispatch, history) => {
    const typedWindow: any = window;
    window.open(
        `${typedWindow.REACT_APP_REST_API || ""}${typedWindow.REACT_APP_REST_API_NAMESPACE}/${
            data.resourceType
        }/${data.resourceData?.id}?download=export&by=id`
    );
    return Promise.resolve(null);
};
export const exportActionSignature: ActionSignature = {
    key: "export",
    action: ExportAction,
    label: () => "Export",
    icon: () => IconCollection.export,
    disableWhen: isDeleted,
};

export const DownloadAction: Action = (data, dispatch, history) => {
    const typedWindow: any = window;
    window.open(
        `${typedWindow.REACT_APP_REST_API || ""}${typedWindow.REACT_APP_REST_API_NAMESPACE}/${
            data.resourceType
        }/${data.resourceData?.id}?download=true&by=id`
    );
    return Promise.resolve(null);
};
export const downloadActionSignature: ActionSignature = {
    key: "download",
    action: DownloadAction,
    label: () => "Download",
    icon: () => IconCollection.download,
    disableWhen: isDeleted,
};

export const DuplicateAction: Action = (data, dispatch, history) => {
    history.push(`${data.resourceType}/new?duplicatedRecordId=${data.resourceData.id}`);
    return Promise.resolve(null);
};
export const duplicateActionSignature: ActionSignature = {
    key: "duplicate",
    action: DuplicateAction,
    label: () => "Duplicate",
    icon: () => IconCollection.plusCircleMultipleOutline,
    disableWhen: isDeleted,
    omitWhen: (props) => !!props.isHidden,
};

export const MoveToLocationSingleAction: Action = (data, dispatch) => {
    return new Promise((resolve, reject) => {
        const modal = Modal.confirm({
            title: "Move selected resource to:",
            icon: null,
            onOk: () => {},
            onCancel: () => {},
            okText: "",
            cancelText: "",
            okButtonProps: { disabled: true },
            cancelButtonProps: { disabled: true },
            className: "user-auth-modal action-modal",
            centered: true,
        });

        const settlement = createModalActionSettlement(() => modal.destroy(), { resolve, reject });

        const form = (
            <StoreProviderForm store={store}>
                <MoveToLocationForm
                    data={data}
                    dispatch={dispatch}
                    onCancel={settlement.cancel}
                    onSuccess={settlement.succeed}
                    onError={settlement.fail}
                />
            </StoreProviderForm>
        );

        modal.update((previousConfig) => ({
            ...previousConfig,
            content: form,
        }));
    });
};

export const moveToLocationActionSignature: ActionSignature = {
    key: "move_to_location",
    action: MoveToLocationSingleAction,
    label: () => "Move",
    icon: () => IconCollection.folderMoveOutlineIcon,
    disableWhen: getReadOnlyOrDeleted,
    hasAfterAction: true,
};

/* COMMON LIST ACTIONS */
export const MoveToLocationAction = ({ resources, afterAction }) => {
    const [isOpen, setIsOpen] = useState(false);

    const { moveableResources } = classifyResources(resources);

    return (
        <>
            <MoveResourcesToLocation
                isOpen={isOpen}
                resourceListRaw={resources}
                onConfirm={() => {
                    setIsOpen(false);
                }}
                onCancel={() => {
                    setIsOpen(false);
                }}
                afterAction={afterAction}
            />
            <Button
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(true);
                }}
                disabled={!moveableResources.length}
                icon={IconCollection.folderMoveOutlineIcon}
                variation="dark"
                label="Move"
            />
        </>
    );
};

MoveToLocationAction.defaultProps = {
    afterAction: () => {},
};

MoveToLocationAction.propTypes = {
    resources: PropTypes.arrayOf(
        PropTypes.shape({
            resourceName: PropTypes.string,
            resourceData: PropTypes.any,
        })
    ).isRequired,
    afterAction: PropTypes.func,
};

const isExecutionList = (resources: any) =>
    resources.some((resource) => resource.resourceData.resource_type === "execution");

export const DeleteListAction = ({
    resources,
    afterAction,
    isDisabled,
    label,
    parameters,
}: any) => {
    const dispatch = useDispatch();
    const normalResources = getNormalObjects(resources);
    const groupedResources = getGroupedResources(normalResources);
    const [isLoading, setLoading] = useState(false);

    const deleteResources = (res: any) => {
        setLoading(true);

        new Promise((resolve, reject) => {
            dispatch({
                type: resourcesActions.START_DELETE_RESOURCES,
                promise: { resolve, reject },
                resources: res,
                parameters,
            });
        })
            .then((result) => {
                if (isExecutionList(resources)) {
                    createNotification(
                        asyncActions.moveToTrash,
                        resources,
                        result as ListActionResult[],
                        true
                    );
                } else {
                    createNotification(
                        asyncActions.moveToTrash,
                        resources,
                        result as ListActionResult[]
                    );
                }
            })
            .catch(console.error)
            .finally(() => {
                setLoading(false);
                postDelete(
                    dispatch,
                    normalResources?.some(({ resourceName }) => resourceName === "project")
                );
                afterAction();
            });
    };

    return (
        <Button
            onClick={() => {
                deleteResources(groupedResources.allowedDeleted);
            }}
            loading={isLoading}
            disabled={isDisabled || !groupedResources.allowedDeleted.length}
            icon={IconCollection.deleteOutline}
            variation="dark"
            label={label ?? "Move to trash"}
        />
    );
};
DeleteListAction.propTypes = {
    resources: PropTypes.arrayOf(
        PropTypes.shape({
            resourceName: PropTypes.string,
            resourceData: PropTypes.any,
        })
    ).isRequired,
    afterAction: PropTypes.func.isRequired,
    isDisabled: PropTypes.bool,
};

export const PermanentlyDeleteListAction = ({ resources, afterAction, label, parameters }: any) => {
    const [isLoading, setLoading] = useState(false);

    const dispatch = useDispatch();
    const normalResources = getNormalObjects(resources);

    const excludeReadOnlyResources = resources?.filter(
        (resource: any) =>
            !resource?.resourceData.is_readonly &&
            !resource?.resourceData.is_project_readonly &&
            !resource?.resourceData.is_bundle_readonly
    );
    const readOnlyCount = resources.length - excludeReadOnlyResources.length;

    const deleteResources = (res: any) => {
        setLoading(true);

        new Promise((resolve, reject) => {
            dispatch({
                type: resourcesActions.START_PERMANENTLY_DELETE_RESOURCES,
                promise: { resolve, reject },
                resources: res,
                parameters,
            });
        })
            .then((result) => {
                if (isExecutionList(resources)) {
                    createNotification(
                        asyncActions.permanentlyDelete,
                        resources,
                        result as ListActionResult[],
                        true
                    );
                } else {
                    createNotification(
                        asyncActions.permanentlyDelete,
                        resources,
                        result as ListActionResult[]
                    );
                }
            })
            .catch(console.error)
            .finally(() => {
                setLoading(false);

                postDelete(
                    dispatch,
                    normalResources?.some(({ resourceName }) => resourceName === "project")
                );
                afterAction();
            });
    };

    const showModal = (resourceList: any[]) => {
        const hasContainer = resourceList?.some(
            ({ resourceName }: any) => resourceName === "project" || resourceName === "bundle"
        );

        Modal.confirm({
            title: "Delete permanently",
            content: (
                <div>
                    Are you sure you want to permanently delete the following {resourceList.length}{" "}
                    items?
                    <ul>
                        {resourceList.map(({ resourceName, resourceData }: any) => (
                            <li
                                key={`${resourceName}-${resourceData?.id}`}
                            >{`${resourceData?.resource_type?.toLowerCase()} "${
                                resourceData?.name
                            }"`}</li>
                        ))}
                    </ul>
                    {hasContainer
                        ? getProjectPermanentDeleteWarning({
                              multiple: true,
                          })
                        : null}
                    {readOnlyCount ? (
                        <div>{`${readOnlyCount} resource${
                            readOnlyCount > 1 ? "s" : ""
                        } could not be deleted permanently as ${
                            readOnlyCount > 1 ? "they are" : "it is"
                        } read_only`}</div>
                    ) : null}
                </div>
            ),
            icon: null,
            onOk() {
                deleteResources(resourceList);
            },
            onCancel() {},
            okText: hasContainer
                ? `Delete container, resources and git repo`
                : "Delete permanently",
            cancelButtonProps: cancelButtonProps,
            okButtonProps: { className: "button button--danger" },
            className: "confirmationModal",
            centered: true,
        });
    };

    return (
        <Button
            onClick={() => showModal(excludeReadOnlyResources)}
            loading={isLoading}
            disabled={!excludeReadOnlyResources.length}
            icon={IconCollection.deleteAlertOutline}
            variation="dark"
            label={label ?? "Delete permanently"}
        />
    );
};
PermanentlyDeleteListAction.propTypes = {
    resources: PropTypes.arrayOf(
        PropTypes.shape({
            resourceName: PropTypes.string,
            resourceData: PropTypes.any,
        })
    ).isRequired,
    afterAction: PropTypes.func.isRequired,
};

export const UndeleteListAction = ({ resources, afterAction, label, parameters }: any) => {
    const [isLoading, setLoading] = useState(false);
    const dispatch = useDispatch();

    const deletedResources = resources.filter((resource: any) => resource.resourceData.deleted_at);

    const undeleteResources = (res: any) => {
        setLoading(true);

        new Promise((resolve, reject) => {
            dispatch({
                type: resourcesActions.START_UNDELETE_RESOURCES,
                promise: { resolve, reject },
                resources: res,
                parameters,
            });
        })
            .then((result) => {
                if (isExecutionList(resources)) {
                    createNotification(
                        asyncActions.restore,
                        resources,
                        result as ListActionResult[],
                        true
                    );
                } else {
                    createNotification(
                        asyncActions.restore,
                        resources,
                        result as ListActionResult[]
                    );
                }
            })
            .catch(console.error)
            .finally(() => {
                setLoading(false);
                afterAction();
            });
    };

    return (
        <Button
            onClick={() => undeleteResources(deletedResources)}
            loading={isLoading}
            disabled={!deletedResources.length}
            icon={IconCollection.deleteRestore}
            variation="dark"
            label={label ?? "Restore"}
        />
    );
};
UndeleteListAction.propTypes = {
    resources: PropTypes.arrayOf(
        PropTypes.shape({
            resourceName: PropTypes.string,
            resourceData: PropTypes.any,
        })
    ).isRequired,
    afterAction: PropTypes.func.isRequired,
};

const runClearListAction = (
    resources: WrappedResource[],
    action: "clear_delete" | "clear_restore",
    permanently: boolean | undefined,
    dispatch: Dispatch,
    afterAction: () => void,
    successMessage: string
) => {
    return runListActionWithProgress(dispatch, resources, successMessage, (r) =>
        clearAction(
            { resourceType: r.resourceName, resourceData: r.resourceData } as ActionData,
            action,
            permanently !== undefined ? { permanently } : {}
        )
    )
        .then(() => {
            forceRecordUpdate(dispatch);
            NotificationService.success(
                resources.length === 1
                    ? successMessage
                    : `${resources.length} items: ${successMessage}`
            );
            afterAction();
        })
        .catch(console.error);
};

export const ClearDeleteListAction = ({ resources, afterAction }: any) => {
    const [isLoading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const applicable = (resources || []).filter(
        (r: any) => r?.resourceData?.pending_delete_identity_id != null
    );

    return (
        <Button
            onClick={() => {
                setLoading(true);
                runClearListAction(
                    applicable,
                    "clear_delete",
                    true,
                    dispatch,
                    () => {
                        setLoading(false);
                        afterAction();
                    },
                    "Pending delete cleared"
                );
            }}
            loading={isLoading}
            disabled={!applicable.length}
            icon={IconCollection.deleteAlertOutline}
            variation="clear"
            label="Clear pending delete"
        />
    );
};
ClearDeleteListAction.propTypes = {
    resources: PropTypes.arrayOf(
        PropTypes.shape({
            resourceName: PropTypes.string,
            resourceData: PropTypes.any,
        })
    ).isRequired,
    afterAction: PropTypes.func,
};

export const ClearRestoreListAction = ({ resources, afterAction }: any) => {
    const [isLoading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const applicable = (resources || []).filter(
        (r: any) => r?.resourceData?.pending_restore_identity_id != null
    );

    return (
        <Button
            onClick={() => {
                setLoading(true);
                runClearListAction(
                    applicable,
                    "clear_restore",
                    undefined,
                    dispatch,
                    () => {
                        setLoading(false);
                        afterAction();
                    },
                    "Pending restore cleared"
                );
            }}
            loading={isLoading}
            disabled={!applicable.length}
            icon={IconCollection.deleteRestore}
            variation="clear"
            label="Clear pending restore"
        />
    );
};
ClearRestoreListAction.propTypes = {
    resources: PropTypes.arrayOf(
        PropTypes.shape({
            resourceName: PropTypes.string,
            resourceData: PropTypes.any,
        })
    ).isRequired,
    afterAction: PropTypes.func,
};

export const ClearTrashListAction = ({ resources, afterAction }: any) => {
    const [isLoading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const applicable = (resources || []).filter(
        (r: any) => r?.resourceData?.pending_trash_identity_id != null
    );

    return (
        <Button
            onClick={() => {
                setLoading(true);
                runClearListAction(
                    applicable,
                    "clear_delete",
                    false,
                    dispatch,
                    () => {
                        setLoading(false);
                        afterAction();
                    },
                    "Pending move to trash cleared"
                );
            }}
            loading={isLoading}
            disabled={!applicable.length}
            icon={IconCollection.deleteOutline}
            variation="clear"
            label="Clear pending move to trash"
        />
    );
};
ClearTrashListAction.propTypes = {
    resources: PropTypes.arrayOf(
        PropTypes.shape({
            resourceName: PropTypes.string,
            resourceData: PropTypes.any,
        })
    ).isRequired,
    afterAction: PropTypes.func,
};

const ReadOnlyListAction = ({ resourceList, afterAction, isAddReadOnly }: any) => {
    const [isLoading, setLoading] = useState(false);
    const dispatch = useDispatch();

    const readOnlyAction = (res: any) => {
        setLoading(true);

        return new Promise((resolve, reject) => {
            dispatch({
                type: resourcesActions.START_UPDATE_RESOURCES,
                promise: { resolve, reject },
                resources: res,
                variables: {
                    is_readonly: isAddReadOnly,
                },
            });
        })
            .then((result) => {
                createNotification(
                    isAddReadOnly ? "set to read-only" : "remove read-only",
                    res,
                    result as ListActionResult[]
                );
            })
            .catch(console.error)
            .finally(() => {
                setLoading(false);
                afterAction();
            });
    };

    return (
        <Button
            onClick={() => readOnlyAction(resourceList)}
            loading={isLoading}
            disabled={!resourceList.length}
            icon={
                isAddReadOnly ? IconCollection.lockOutline : IconCollection.lockOpenVariantOutline
            }
            variation="dark"
            label={isAddReadOnly ? "Set to read-only" : "Remove read-only"}
        />
    );
};

export const AddReadOnlyListAction = ({ resources, afterAction }: any) => {
    const normalResources = getNormalObjects(resources);

    return ReadOnlyListAction({ resourceList: normalResources, afterAction, isAddReadOnly: true });
};
export const RemoveReadOnlyListAction = ({ resources, afterAction }: any) => {
    const resourceList = resources.filter(
        (resource) =>
            resource.resourceData?.is_readonly &&
            !resource.resourceData?.is_project_readonly &&
            !resource.resourceData?.is_bundle_readonly
    );

    return ReadOnlyListAction({ resourceList, afterAction, isAddReadOnly: false });
};
AddReadOnlyListAction.propTypes = {
    resources: PropTypes.arrayOf(
        PropTypes.shape({
            resourceName: PropTypes.string,
            resourceData: PropTypes.any,
        })
    ).isRequired,
    afterAction: PropTypes.func.isRequired,
};

const TrackOrIgnoreInGitListAction = ({ resourceList, afterAction, canTrackInGit }: any) => {
    const [isLoading, setLoading] = useState(false);
    const dispatch = useDispatch();

    const trackInGitAction = (res: any) => {
        setLoading(true);

        return new Promise((resolve, reject) => {
            dispatch({
                type: resourcesActions.START_UPDATE_RESOURCES,
                promise: { resolve, reject },
                resources: res,
                variables: {
                    track_in_git: canTrackInGit,
                },
            });
        })
            .then((result) => {
                createNotification(
                    canTrackInGit ? "track in git" : "ignore in git",
                    res,
                    result as ListActionResult[]
                );
            })
            .catch(console.error)
            .finally(() => {
                setLoading(false);
                afterAction();
            });
    };

    return (
        <Button
            onClick={() => trackInGitAction(resourceList)}
            loading={isLoading}
            disabled={!resourceList.length}
            icon={IconCollection.git}
            variation="dark"
            label={canTrackInGit ? "Track in git" : "Ignore in git"}
        />
    );
};

export const TrackInGitListAction = ({ resources, afterAction }: any) => {
    const normalResources = getNormalObjects(resources);

    return TrackOrIgnoreInGitListAction({
        resourceList: normalResources,
        afterAction,
        canTrackInGit: true,
    });
};
export const IgnoreInGitListAction = ({ resources, afterAction }: any) => {
    const normalResources = getNormalObjects(resources);

    return TrackOrIgnoreInGitListAction({
        resourceList: normalResources,
        afterAction,
        canTrackInGit: false,
    });
};

const SetBundleContentListAction = ({ resourceList, afterAction, isBundleContent }: any) => {
    const [isLoading, setLoading] = useState(false);
    const dispatch = useDispatch();

    const setBundleContent = (res: any) => {
        setLoading(true);

        return new Promise((resolve, reject) => {
            dispatch({
                type: resourcesActions.START_UPDATE_RESOURCES,
                promise: { resolve, reject },
                resources: res,
                variables: {
                    is_bundle_content: isBundleContent,
                },
            });
        })
            .then((result) => {
                createNotification(
                    isBundleContent ? "set bundle content" : "remove bundle content",
                    res,
                    result as ListActionResult[]
                );
            })
            .catch(console.error)
            .finally(() => {
                setLoading(false);
                afterAction();
            });
    };

    return (
        <Button
            onClick={() => setBundleContent(resourceList)}
            loading={isLoading}
            disabled={!resourceList.length}
            icon={
                isBundleContent
                    ? IconCollection.folderTableOutline
                    : IconCollection.folderRemoveOutline
            }
            variation="dark"
            label={isBundleContent ? "Set bundle content" : "Remove bundle content"}
        />
    );
};

export const BundleContentListAction = ({ resources, afterAction }: any) => {
    const normalResources = getNormalObjects(resources).filter(
        (resource) => resource.resourceData?.bundle_id && !resource.resourceData?.is_bundle_content
    );

    return SetBundleContentListAction({
        resourceList: normalResources,
        afterAction,
        isBundleContent: true,
    });
};
export const RemoveBundleContentListAction = ({ resources, afterAction }: any) => {
    const normalResources = getNormalObjects(resources).filter(
        (resource) => resource.resourceData?.bundle_id && resource.resourceData?.is_bundle_content
    );

    return SetBundleContentListAction({
        resourceList: normalResources,
        afterAction,
        isBundleContent: false,
    });
};

export const bundleContentActions = (selectedItems, afterButtonAction) => (
    <>
        <li key="set bundle content">
            <BundleContentListAction resources={selectedItems} afterAction={afterButtonAction} />
        </li>
        <li key="remove bundle content">
            <RemoveBundleContentListAction
                resources={selectedItems}
                afterAction={afterButtonAction}
            />
        </li>
    </>
);

const hasResourceDeprecations = (resource) => resource.resourceData.has_deprecation;

export const MigrateDeprecationsListAction = ({ resources, afterAction }: any) => {
    const dispatch = useDispatch();

    const resourceList = resources.filter(
        (resource) => getSingleNormalObject(resource) && hasResourceDeprecations(resource)
    );

    const migrateDeprecationAction = (res: any) => {
        return new Promise((resolve, reject) => {
            dispatch({
                type: resourcesActions.START_MIGRATE_DEPRECATIONS_RESOURCES,
                promise: { resolve, reject },
                resources: res,
            });
        })
            .then((data) => {
                const { successfulItems, partiallySuccessfulItems, failedItems } = (
                    data as ListActionResult[]
                ).reduce(
                    (acc, { success, error, resource }) => {
                        if (success && !error) {
                            return { ...acc, successfulItems: acc.successfulItems + 1 };
                        } else if (success && error) {
                            return {
                                ...acc,
                                partiallySuccessfulItems: acc.partiallySuccessfulItems + 1,
                            };
                        } else {
                            return {
                                ...acc,
                                failedItems: {
                                    ...acc.failedItems,
                                    [resource.resourceData.name]: error,
                                },
                            };
                        }
                    },
                    {
                        successfulItems: 0,
                        partiallySuccessfulItems: 0,
                        failedItems: {},
                    }
                );
                const getPluralEnding = (count) => (count > 1 ? "s" : "");
                const successfulNotification =
                    successfulItems > 0
                        ? `${successfulItems} object${getPluralEnding(
                              successfulItems
                          )} migrated deprecations successfully.`
                        : "";
                const partiallySuccessfulNotification =
                    partiallySuccessfulItems > 0
                        ? ` ${partiallySuccessfulItems} object${getPluralEnding(
                              partiallySuccessfulItems
                          )} migrated deprecations partially.`
                        : "";
                const failedNotification =
                    Object.values(failedItems).length > 0
                        ? ` ${Object.values(failedItems).length} object${getPluralEnding(
                              Object.values(failedItems).length
                          )} couldn't migrate deprecations.`
                        : "";
                const notification = `${successfulNotification}${partiallySuccessfulNotification}${failedNotification}`;
                const expandableNotification = (
                    <ExpandableNotification
                        title={notification}
                        details={
                            Object.values(failedItems).length ? (
                                <ul>
                                    {Object.entries(failedItems).map(([name, error]: any) => (
                                        <li key={name}>
                                            {name}:{" "}
                                            {
                                                <Markdown
                                                    markdownString={error.message ?? error}
                                                    className="notificationMarkdown"
                                                />
                                            }
                                        </li>
                                    ))}
                                </ul>
                            ) : null
                        }
                        detailsForClipboard={JSON.stringify(
                            Object.entries(failedItems).map(([name, error]: any) => ({
                                [name]: error.message ?? error,
                            }))
                        )}
                    />
                );
                if (successfulItems === Object.values(data as ListActionResult[]).length) {
                    NotificationService.success(successfulNotification);
                } else if (successfulNotification || partiallySuccessfulNotification) {
                    NotificationService.info(expandableNotification);
                } else {
                    NotificationService.error(expandableNotification);
                }
            })
            .catch(console.error)
            .finally(() => {
                afterAction();
            });
    };
    return (
        <Button
            onClick={() => migrateDeprecationAction(resourceList)}
            disabled={
                !resourceList.length ||
                resourceList.every((resource) => !hasResourceDeprecations(resource))
            }
            icon={IconCollection.update}
            variation="dark"
            label="Migrate deprecations"
        />
    );
};

MigrateDeprecationsListAction.defaultProps = {
    afterAction: () => {},
};
MigrateDeprecationsListAction.propTypes = {
    resources: PropTypes.arrayOf(
        PropTypes.shape({
            resourceName: PropTypes.string,
            resourceData: PropTypes.any,
        })
    ).isRequired,
    afterAction: PropTypes.func,
};

export const RemoveIdentityListAction = ({ resources, afterAction }: any) => {
    const dispatch = useDispatch();
    const [isLoading, setLoading] = useState(false);

    const removeIdentityRole = (resourceList: any) => {
        setLoading(true);

        // Collect a per-item outcome so a partial failure (e.g. the backend
        // refusing to remove a role from the caller's OWN identity) is reported
        // as a single summary notification instead of a bare error, and so the
        // action always settles to a clean finished state (no stuck button /
        // progress bar). Errors are handled per item; they must NOT propagate
        // out of the worker, otherwise runWithConcurrency would abort the pool.
        const results: ListActionResult[] = [];

        return runListActionWithProgress(
            dispatch,
            resourceList,
            "Removing from role",
            async (resource: any) => {
                try {
                    await new Promise((resolve, reject) => {
                        dispatch({
                            type: actions.START_DELETE_IDENTITYROLE,
                            promise: { resolve, reject },
                            silent: true,
                            params: {
                                query: deleteMutation,
                                variables: { id: resource.resourceData.id },
                            },
                        });
                    });
                    results.push({ resource, success: true });
                } catch (error) {
                    results.push({ resource, success: false, error });
                }
            }
        )
            .catch(console.error)
            .finally(() => {
                createNotification("removed", resourceList, results);
                setLoading(false);
                afterAction();
            });
    };

    return (
        <Button
            onClick={() => removeIdentityRole(resources)}
            loading={isLoading}
            disabled={!resources?.length}
            icon={IconCollection.deleteRestore}
            variation="dark"
            label="Remove from role"
        />
    );
};
RemoveIdentityListAction.defaultProps = {
    afterAction: () => {},
};
RemoveIdentityListAction.propTypes = {
    resources: PropTypes.arrayOf(
        PropTypes.shape({
            resourceName: PropTypes.string,
            resourceData: PropTypes.any,
        })
    ).isRequired,
    afterAction: PropTypes.func,
};

export const RemoveRelationshipListAction = ({
    resources,
    afterAction,
    isDisabled,
    actionButtonLabel,
}: any) => {
    const dispatch = useDispatch();
    const [isLoading, setLoading] = useState(false);

    const deleteResources = (res: any) => {
        setLoading(true);

        new Promise((resolve, reject) => {
            dispatch({
                type: resourcesActions.START_DELETE_RESOURCES,
                promise: { resolve, reject },
                resources: res,
            });
        })
            .then((result) => {
                createNotification("removed", resources, result as ListActionResult[]);
            })
            .catch(console.error)
            .finally(() => {
                setLoading(false);
                afterAction();
            });
    };

    return (
        <Button
            onClick={() => {
                deleteResources(resources);
            }}
            loading={isLoading}
            disabled={isDisabled || !resources?.length}
            icon={IconCollection.deleteOutline}
            variation="white"
            label={`Remove ${actionButtonLabel ?? ""}`}
        />
    );
};
